<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Activity, ChevronDown, ChevronUp, Crosshair, Network, Radar, Sparkles, TrendingUp, UsersRound } from 'lucide-vue-next'

import CopyableAddress from '@/components/CopyableAddress.vue'
import ExpandableText from '@/components/ExpandableText.vue'
import type { AlphaCallThread, AlphaOutcome, AlphaOverview, AlphaProjectLifecycle, AlphaPropagationCluster, AlphaPropagationEvent, CryptoSignal, EarlyDiscovererScore, EarlyProjectScore, SenderAlphaScore, SenderKind } from '@/types/monitor'
import { alphaContractAddressMatches, canonicalAlphaChain } from '@/utils/alphaIdentity'
import { compactAddress, formatLocalDateTime, resolvePublicMediaPath } from '@/utils/format'

const props = defineProps<{
  overview: AlphaOverview | null
  loading: boolean
}>()

const { t } = useI18n()

const callersExpanded = ref(false)
const recommendationsExpanded = ref(false)
const expandedPropagationId = ref<string | null>(null)
const senderRankings = computed(() => props.overview?.sender_rankings ?? [])
const allRecommendations = computed(() => props.overview?.recommendations ?? [])
const topSenders = computed(() => senderRankings.value.slice(0, callersExpanded.value ? 20 : 5))
const recommendations = computed(() => allRecommendations.value.slice(0, recommendationsExpanded.value ? 20 : 5))
const earlyProjectScoreMap = computed(() => new Map(
  (props.overview?.early_project_scores ?? []).map((score) => [score.signal_id, score]),
))
const earlyProjects = computed(() => [...(props.overview?.early_projects ?? [])]
  .sort((left, right) => {
    const leftScore = earlyProjectScoreMap.value.get(left.id)?.early_score ?? 0
    const rightScore = earlyProjectScoreMap.value.get(right.id)?.early_score ?? 0
    return rightScore - leftScore || Date.parse(right.observed_at) - Date.parse(left.observed_at)
  })
  .slice(0, 8))
const earlyDiscoverers = computed(() => props.overview?.early_discoverer_rankings.slice(0, 5) ?? [])
const projectLifecycles = computed(() => props.overview?.project_lifecycles.slice(0, 12) ?? [])
const propagationClusters = computed(() => props.overview?.propagation_clusters.slice(0, 12) ?? [])
const callThreads = computed(() => props.overview?.call_threads.slice(0, 10) ?? [])
const recentSignals = computed(() => props.overview?.recent_signals.slice(0, 18) ?? [])
const canExpandCallers = computed(() => senderRankings.value.length > 5)
const canExpandRecommendations = computed(() => allRecommendations.value.length > 5)

function senderName(sender: SenderAlphaScore): string {
  return sender.name?.trim() || sender.username?.trim() || String(sender.telegram_id)
}

function discovererName(sender: EarlyDiscovererScore): string {
  return sender.name?.trim() || sender.username?.trim() || String(sender.telegram_id)
}

function signalSender(signal: CryptoSignal): string {
  if (signal.sender_username) return `@${signal.sender_username}`
  return signal.sender_name?.trim() || t('alpha.unknownSender')
}

function senderKindLabel(kind: SenderKind): string {
  switch (kind) {
    case 'bot': return t('workspace.botTool')
    case 'channel': return t('workspace.channelSender')
    case 'group': return t('workspace.groupSender')
    case 'human': return t('workspace.humans')
    default: return t('workspace.unknownSenderKind')
  }
}

function signalEntityLabel(signal: CryptoSignal): string {
  return signal.asset_symbol?.trim() || signal.project_name?.trim() || (signal.contract_address ? compactAddress(signal.contract_address) : '') || '—'
}

function recommendationEntityLabel(item: AlphaOverview['recommendations'][number]): string {
  return item.asset_symbol?.trim() || item.project_name?.trim() || (item.contract_address ? compactAddress(item.contract_address) : '') || '—'
}

function propagationEntityLabel(cluster: AlphaPropagationCluster): string {
  return cluster.asset_symbol?.trim() || cluster.project_name?.trim() || (cluster.contract_address ? compactAddress(cluster.contract_address) : '') || '—'
}

function propagationIndependence(cluster: AlphaPropagationCluster): string {
  if (!cluster.total_signals) return '—'
  return `${Math.round((cluster.independent_signal_count / cluster.total_signals) * 100)}%`
}

function propagationRisk(cluster: AlphaPropagationCluster): string {
  const parts: string[] = []
  if (cluster.duplicate_signal_count) parts.push(`${cluster.duplicate_signal_count} ${t('alpha.duplicates')}`)
  if (cluster.near_duplicate_signal_count) parts.push(`${cluster.near_duplicate_signal_count} ${t('alpha.nearDuplicates')}`)
  if (cluster.copy_risk_signal_count) parts.push(`${cluster.copy_risk_signal_count} ${t('alpha.copyRisk')}`)
  if (cluster.coordinated_shill_signal_count) parts.push(`${cluster.coordinated_shill_signal_count} ${t('alpha.coordinatedRisk')}`)
  return parts.join(' · ') || t('alpha.noPropagationRisk')
}

function togglePropagation(cluster: AlphaPropagationCluster): void {
  expandedPropagationId.value = expandedPropagationId.value === cluster.first_signal_id
    ? null
    : cluster.first_signal_id
}

function propagationEventSender(event: AlphaPropagationEvent): string {
  if (event.sender_username) return `@${event.sender_username}`
  return event.sender_name?.trim() || event.sender_telegram_id?.toString() || t('alpha.unknownSender')
}

function propagationClassification(event: AlphaPropagationEvent): string {
  switch (event.classification) {
    case 'independent': return t('alpha.classificationIndependent')
    case 'duplicate': return t('alpha.classificationDuplicate')
    case 'near_duplicate': return t('alpha.classificationNearDuplicate')
    case 'copy_risk': return t('alpha.copyRisk')
    case 'coordinated_shill': return t('alpha.coordinatedRisk')
    default: return event.classification
  }
}

function propagationSimilarity(event: AlphaPropagationEvent): string {
  if (event.text_similarity === null) return ''
  return t('alpha.textSimilarity', { count: Math.round(event.text_similarity * 100) })
}

function supportingEvidence(event: AlphaPropagationEvent) {
  return event.evidence_messages.filter((message) => message.message_id !== event.message_id)
}

function propagationForSignal(signal: CryptoSignal): AlphaPropagationCluster | null {
  const clusters = props.overview?.propagation_clusters ?? []
  return clusters.find((cluster) => {
    if (signal.contract_address && cluster.contract_address) {
      return canonicalAlphaChain(signal.chain) === canonicalAlphaChain(cluster.chain)
        && alphaContractAddressMatches(signal.chain, signal.contract_address, cluster.contract_address)
    }
    if (signal.asset_symbol && cluster.asset_symbol) {
      return canonicalAlphaChain(signal.chain) === canonicalAlphaChain(cluster.chain)
        && signal.asset_symbol.toUpperCase() === cluster.asset_symbol.toUpperCase()
    }
    return Boolean(
      signal.project_name
      && cluster.project_name
      && signal.project_name.trim().toLowerCase() === cluster.project_name.trim().toLowerCase(),
    )
  }) ?? null
}

function firstSeenSender(cluster: AlphaPropagationCluster): string {
  if (cluster.first_sender_username) return `@${cluster.first_sender_username}`
  return cluster.first_sender_name?.trim() || t('alpha.unknownSender')
}

function propagationWindow(minutes: number): string {
  if (minutes < 60) return t('alpha.propagationMinutes', { count: Math.max(0, minutes) })
  if (minutes < 1440) return t('alpha.propagationHours', { count: Math.round(minutes / 60) })
  return t('alpha.propagationDays', { count: Math.round(minutes / 1440) })
}

function propagationSummary(signal: CryptoSignal): string {
  const cluster = propagationForSignal(signal)
  if (!cluster) return ''
  return `${t('alpha.monitoredFirstSeen')} ${firstSeenSender(cluster)} · ${cluster.independent_senders} ${t('alpha.independentPeople')} · ${cluster.independent_sources} ${t('alpha.sources')} · ${cluster.duplicate_signal_count} ${t('alpha.duplicates')} · ${cluster.near_duplicate_signal_count} ${t('alpha.nearDuplicates')} · ${propagationWindow(cluster.propagation_minutes)}`
}

function threadSender(thread: AlphaCallThread): string {
  if (thread.sender_username) return `@${thread.sender_username}`
  return thread.sender_name?.trim() || String(thread.sender_telegram_id)
}

function threadEntity(thread: AlphaCallThread): string {
  return thread.asset_symbol?.trim() || thread.project_name?.trim() || (thread.contract_address ? compactAddress(thread.contract_address) : '') || '—'
}

function threadOutcome(thread: AlphaCallThread): AlphaOutcome | null {
  return thread.outcomes.reduce<AlphaOutcome | null>(
    (best, current) => (!best || current.horizon_seconds > best.horizon_seconds ? current : best),
    null,
  )
}

function threadOutcomeSummary(thread: AlphaCallThread): string {
  const outcome = threadOutcome(thread)
  if (!outcome) return t('alpha.threadAwaitingOutcome')
  return `${horizonLabel(outcome.horizon_seconds)} ${formatSignedPercent(outcome.directional_return_pct)}`
}

function formatPercent(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`
}

function formatScore(value: number): string {
  return value.toFixed(value >= 100 ? 0 : 1)
}

function formatCompactUsd(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—'
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toFixed(value >= 10 ? 0 : 2)}`
}

function projectMarketFacts(signal: CryptoSignal): string {
  const facts: string[] = []
  if (signal.dex_liquidity_usd !== null) facts.push(`${t('alpha.liquidity')} ${formatCompactUsd(signal.dex_liquidity_usd)}`)
  const valuation = signal.dex_market_cap_usd ?? signal.dex_fdv_usd
  if (valuation !== null) facts.push(`${t('alpha.valuation')} ${formatCompactUsd(valuation)}`)
  if ((signal.dex_boosts_active ?? 0) > 0) facts.push(`${t('alpha.boosts')} ${signal.dex_boosts_active}`)
  return facts.join(' · ')
}

function earlyProjectScore(signal: CryptoSignal): EarlyProjectScore | null {
  return earlyProjectScoreMap.value.get(signal.id) ?? null
}

function pairAgeAtDiscovery(score: EarlyProjectScore): string {
  const minutes = score.pair_age_at_discovery_minutes
  if (minutes === null) return t('alpha.pairAgeUnknown')
  if (minutes < 0) return t('alpha.beforePair', { count: Math.abs(minutes) })
  if (minutes < 60) return t('alpha.afterPairMinutes', { count: minutes })
  if (minutes < 1440) return t('alpha.afterPairHours', { count: Math.round(minutes / 60) })
  return t('alpha.afterPairDays', { count: Math.round(minutes / 1440) })
}

function medianDiscoveryTiming(minutes: number | null): string {
  if (minutes === null) return t('alpha.pairAgeUnknown')
  if (minutes < 0) return t('alpha.beforePair', { count: Math.abs(minutes) })
  if (minutes < 60) return t('alpha.afterPairMinutes', { count: minutes })
  if (minutes < 1440) return t('alpha.afterPairHours', { count: Math.round(minutes / 60) })
  return t('alpha.afterPairDays', { count: Math.round(minutes / 1440) })
}

function earlyScoreDetails(signal: CryptoSignal): string {
  const score = earlyProjectScore(signal)
  if (!score) return ''
  return `${pairAgeAtDiscovery(score)} · ${t('alpha.timing')} ${score.timing_score.toFixed(0)} · ${t('alpha.independence')} ${score.independence_score.toFixed(0)} · ${t('alpha.market')} ${score.market_score.toFixed(0)} · ${t('alpha.riskPenalty')} -${score.risk_penalty.toFixed(0)}`
}

function latestOutcome(signal: CryptoSignal): AlphaOutcome | null {
  const outcomes = props.overview?.outcomes[signal.id] ?? []
  return outcomes.reduce<AlphaOutcome | null>(
    (best, current) => (!best || current.horizon_seconds > best.horizon_seconds ? current : best),
    null,
  )
}

function outcomeAt(signal: CryptoSignal, horizonSeconds: number): AlphaOutcome | null {
  return (props.overview?.outcomes[signal.id] ?? [])
    .find((outcome) => outcome.horizon_seconds === horizonSeconds) ?? null
}

function projectPairAge(signal: CryptoSignal): string {
  const score = earlyProjectScore(signal)
  return score ? pairAgeAtDiscovery(score) : t('alpha.pairAgeUnknown')
}

function projectValidationLabel(signal: CryptoSignal, horizonSeconds: number): string {
  const outcome = outcomeAt(signal, horizonSeconds)
  if (outcome) return formatSignedPercent(outcome.directional_return_pct)
  if (signal.entry_price !== null) return t('alpha.validationPending')
  return t('alpha.validationUnavailable')
}

function projectValidationClass(signal: CryptoSignal, horizonSeconds: number): string {
  const outcome = outcomeAt(signal, horizonSeconds)
  if (!outcome) return 'pending'
  if (outcome.directional_return_pct > 0) return 'positive'
  if (outcome.directional_return_pct < 0) return 'negative'
  return 'flat'
}

function projectExcursion(signal: CryptoSignal): string {
  const outcome = outcomeAt(signal, 604_800) ?? outcomeAt(signal, 86_400) ?? latestOutcome(signal)
  if (!outcome) return '—'
  const values: string[] = []
  if (outcome.mfe_pct !== null) values.push(`MFE ${formatSignedPercent(outcome.mfe_pct)}`)
  if (outcome.mae_pct !== null) values.push(`MAE ${formatSignedPercent(outcome.mae_pct)}`)
  return values.join(' · ') || '—'
}

function projectValidationSource(signal: CryptoSignal): string {
  const outcome = outcomeAt(signal, 604_800) ?? outcomeAt(signal, 86_400) ?? latestOutcome(signal)
  return outcome?.pricing_source || '—'
}

function lifecycleEntity(lifecycle: AlphaProjectLifecycle): string {
  return lifecycle.asset_symbol?.trim() || lifecycle.project_name?.trim() || (lifecycle.contract_address ? compactAddress(lifecycle.contract_address) : '') || '—'
}

function lifecycleDiscoverer(lifecycle: AlphaProjectLifecycle): string {
  if (lifecycle.first_discoverer_username) return `@${lifecycle.first_discoverer_username}`
  return lifecycle.first_discoverer_name?.trim() || lifecycle.first_discoverer_telegram_id?.toString() || t('alpha.unknownSender')
}

function lifecycleStateLabel(state: string): string {
  return t(`alpha.lifecycleState.${state}`, state)
}

function lifecycleMilestoneLabel(type: string): string {
  return t(`alpha.lifecycleMilestone.${type}`, type)
}

function lifecycleMarket(lifecycle: AlphaProjectLifecycle): string {
  const parts: string[] = []
  if (lifecycle.entry_price !== null) parts.push(`${t('alpha.entry')} ${lifecycle.entry_price.toPrecision(5)}`)
  if (lifecycle.latest_price !== null) parts.push(`${t('alpha.latest')} ${lifecycle.latest_price.toPrecision(5)}`)
  if (lifecycle.dex_liquidity_usd !== null) parts.push(`${t('alpha.liquidity')} ${formatCompactUsd(lifecycle.dex_liquidity_usd)}`)
  return parts.join(' · ') || '—'
}

function formatSignedPercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—'
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
}

function horizonLabel(seconds: number): string {
  if (seconds >= 604_800) return '7d'
  if (seconds >= 86_400) return '24h'
  return '1h'
}

function outcomeSummary(signal: CryptoSignal): string {
  const outcome = latestOutcome(signal)
  if (outcome) {
    return `${horizonLabel(outcome.horizon_seconds)} ${formatSignedPercent(outcome.directional_return_pct)}`
  }
  if (signal.entry_price !== null) return t('alpha.outcomePending')
  if (signal.dex_liquidity_usd !== null) return t('alpha.notExecutableYet')
  return t('alpha.awaitingPrice')
}

function outcomeDetails(signal: CryptoSignal): string {
  const outcome = latestOutcome(signal)
  if (!outcome) return outcomeSummary(signal)
  const parts = [
    `${horizonLabel(outcome.horizon_seconds)} ${formatSignedPercent(outcome.directional_return_pct)}`,
  ]
  if (outcome.mfe_pct !== null) parts.push(`MFE ${formatSignedPercent(outcome.mfe_pct)}`)
  if (outcome.mae_pct !== null) parts.push(`MAE ${formatSignedPercent(outcome.mae_pct)}`)
  parts.push(outcome.pricing_source)
  return parts.join(' · ')
}
</script>

<template>
  <section class="tm-alpha-overview">
    <div class="tm-alpha-heading">
      <div>
        <span class="tm-alpha-kicker"><Radar :size="15" />{{ t('alpha.kicker') }}</span>
        <h2>{{ t('alpha.title') }}</h2>
        <p>{{ t('alpha.description') }}</p>
      </div>
      <span v-if="overview" class="tm-alpha-generated">{{ t('alpha.updated') }} {{ formatLocalDateTime(overview.generated_at) }}</span>
    </div>

    <div v-if="loading && !overview" class="tm-alpha-loading"><Sparkles :size="18" />{{ t('alpha.loading') }}</div>

    <template v-else-if="overview">
      <div class="tm-alpha-stat-grid">
        <div><Crosshair :size="17" /><span>{{ t('alpha.signals') }}</span><strong>{{ overview.recent_signals.length }}</strong></div>
        <div><UsersRound :size="17" /><span>{{ t('alpha.callers') }}</span><strong>{{ overview.sender_rankings.length }}</strong></div>
        <div><TrendingUp :size="17" /><span>{{ t('alpha.consensus') }}</span><strong>{{ overview.recommendations.length }}</strong></div>
        <div><Radar :size="17" /><span>{{ t('alpha.earlyProjects') }}</span><strong>{{ overview.early_projects.length }}</strong></div>
        <div><Network :size="17" /><span>{{ t('alpha.propagationClusters') }}</span><strong>{{ overview.propagation_clusters.length }}</strong></div>
      </div>

      <div class="tm-alpha-grid">
        <article class="tm-card tm-alpha-panel tm-alpha-panel--callers">
          <header><div><UsersRound :size="16" /><strong>{{ t('alpha.topCallers') }}</strong></div><span>{{ t('alpha.qualityHint') }}</span></header>
          <div v-if="topSenders.length" class="tm-alpha-caller-list">
            <div v-for="(sender, index) in topSenders" :key="sender.telegram_id" class="tm-alpha-caller-row">
              <span class="tm-alpha-rank">{{ index + 1 }}</span>
              <span class="tm-alpha-avatar">
                <span>{{ senderName(sender).slice(0, 1).toUpperCase() }}</span>
                <img v-if="sender.avatar_path" :src="resolvePublicMediaPath(sender.avatar_path) || undefined" :alt="senderName(sender)" loading="lazy" />
              </span>
              <span class="tm-alpha-caller-copy">
                <strong>{{ senderName(sender) }}</strong>
                <small>{{ sender.username ? `@${sender.username}` : t('alpha.noUsername') }} · {{ sender.actionable_calls }} {{ t('alpha.calls') }} · {{ sender.project_discoveries }} {{ t('alpha.discoveries') }}</small>
                <small class="tm-alpha-caller-assets">{{ sender.top_assets.join(' / ') || '—' }}</small>
              </span>
              <span class="tm-alpha-caller-metrics">
                <strong>{{ formatScore(sender.quality_score) }}</strong>
                <small :title="`${t('alpha.integrity')} ${formatScore(sender.avg_integrity_score)} · ${t('alpha.structure')} ${formatScore(sender.avg_structure_score)} · ${t('alpha.verifiedSamples')} ${sender.verified_signals}`">24h {{ formatPercent(sender.win_rate_24h) }} · n={{ sender.verified_24h }} · v={{ sender.verified_signals }}</small>
                <small v-if="sender.originality_assessed_signals" class="tm-alpha-caller-originality" :title="`${sender.independent_expression_signals} ${t('alpha.independentEvidence')} / ${sender.originality_assessed_signals} · ${sender.duplicate_or_copy_signals} ${t('alpha.duplicates')}`">
                  {{ t('alpha.independentRate') }} {{ formatPercent(sender.independent_expression_rate) }} · d={{ sender.duplicate_or_copy_signals }}
                </small>
              </span>
            </div>
          </div>
          <div v-else class="tm-alpha-empty">{{ t('alpha.noRankings') }}</div>
          <button v-if="canExpandCallers" type="button" class="tm-alpha-more" @click="callersExpanded = !callersExpanded">
            <component :is="callersExpanded ? ChevronUp : ChevronDown" :size="14" />
            {{ callersExpanded ? t('alpha.collapse') : t('alpha.showMore', { count: senderRankings.length - 5 }) }}
          </button>
        </article>

        <article class="tm-card tm-alpha-panel tm-alpha-panel--consensus">
          <header><div><TrendingUp :size="16" /><strong>{{ t('alpha.recommendations') }}</strong></div><span>{{ t('alpha.independentConsensus') }}</span></header>
          <div v-if="recommendations.length" class="tm-alpha-recommendation-list">
            <div v-for="item in recommendations" :key="`${item.contract_address || item.asset_symbol || item.project_name}-${item.direction}`" class="tm-alpha-recommendation-row">
              <span class="tm-alpha-token">
                <strong v-if="item.asset_symbol?.trim() || item.project_name?.trim()">{{ recommendationEntityLabel(item) }}</strong>
                <CopyableAddress v-else-if="item.contract_address" :value="item.contract_address" />
                <strong v-else>—</strong>
                <small>{{ item.chain || '—' }}</small>
              </span>
              <span class="tm-alpha-direction" :class="`tm-alpha-direction--${item.direction}`">{{ item.direction }}</span>
              <span class="tm-alpha-recommendation-copy">
                <strong>{{ item.independent_senders }} {{ t('alpha.people') }} · {{ item.independent_sources }} {{ t('alpha.sources') }} · {{ item.independent_signal_count }} {{ t('alpha.independentEvidence') }}</strong>
                <small>{{ item.top_senders.join(' · ') || t('alpha.noCaller') }}</small>
                <small v-if="item.duplicate_signal_count || item.near_duplicate_signal_count || item.copy_risk_signal_count" class="tm-alpha-copy-risk">
                  {{ item.signal_count }} {{ t('alpha.signalsShort') }} · {{ item.duplicate_signal_count }} {{ t('alpha.duplicates') }} · {{ item.near_duplicate_signal_count }} {{ t('alpha.nearDuplicates') }} · {{ item.copy_risk_signal_count }} {{ t('alpha.copyRisk') }}
                </small>
                <ExpandableText v-if="item.latest_thesis" class="tm-alpha-expandable-copy" :text="item.latest_thesis" :lines="2" />
              </span>
              <span class="tm-alpha-score">{{ formatScore(item.weighted_score) }}</span>
            </div>
          </div>
          <div v-else class="tm-alpha-empty">{{ t('alpha.noConsensus') }}</div>
          <button v-if="canExpandRecommendations" type="button" class="tm-alpha-more" @click="recommendationsExpanded = !recommendationsExpanded">
            <component :is="recommendationsExpanded ? ChevronUp : ChevronDown" :size="14" />
            {{ recommendationsExpanded ? t('alpha.collapse') : t('alpha.showMore', { count: allRecommendations.length - 5 }) }}
          </button>
        </article>

        <article class="tm-card tm-alpha-panel tm-alpha-panel--projects">
          <header><div><Radar :size="16" /><strong>{{ t('alpha.earlyRadar') }}</strong></div><span>{{ t('alpha.earlyHint') }}</span></header>
          <div v-if="earlyDiscoverers.length" class="tm-alpha-discoverer-label">{{ t('alpha.earlyDiscoverers') }}</div>
          <div v-if="earlyDiscoverers.length" class="tm-alpha-discoverer-strip">
            <div v-for="(sender, index) in earlyDiscoverers" :key="sender.telegram_id" class="tm-alpha-discoverer-item">
              <span class="tm-alpha-rank">{{ index + 1 }}</span>
              <span class="tm-alpha-avatar">
                <span>{{ discovererName(sender).slice(0, 1).toUpperCase() }}</span>
                <img v-if="sender.avatar_path" :src="resolvePublicMediaPath(sender.avatar_path) || undefined" :alt="discovererName(sender)" loading="lazy" />
              </span>
              <span class="tm-alpha-discoverer-copy">
                <strong>{{ discovererName(sender) }}</strong>
                <small>{{ sender.top_projects.join(' / ') || '—' }}</small>
                <small>{{ sender.first_discoveries }} {{ t('alpha.firstDiscoveries') }} · {{ medianDiscoveryTiming(sender.median_pair_age_at_discovery_minutes) }} · {{ sender.pre_pair_discoveries }} {{ t('alpha.prePair') }}</small>
              </span>
              <span class="tm-alpha-discoverer-metrics">
                <strong>{{ sender.early_discovery_score.toFixed(1) }}</strong>
                <small>24h {{ formatPercent(sender.win_rate_24h) }} · n={{ sender.verified_24h }} · v={{ sender.verified_discoveries }}</small>
              </span>
            </div>
          </div>
          <div v-else class="tm-alpha-discoverer-empty">{{ t('alpha.noEarlyDiscoverers') }}</div>
          <div v-if="earlyProjects.length" class="tm-alpha-project-list">
            <div v-for="signal in earlyProjects" :key="signal.id" class="tm-alpha-project-row">
              <span class="tm-alpha-token">
                <strong v-if="signal.asset_symbol?.trim() || signal.project_name?.trim()">{{ signalEntityLabel(signal) }}</strong>
                <CopyableAddress v-else-if="signal.contract_address" :value="signal.contract_address" />
                <strong v-else>—</strong>
                <small>{{ signal.chain || t('alpha.unknownChain') }}</small>
              </span>
              <span class="tm-alpha-project-copy">
                <strong>{{ signalSender(signal) }}</strong>
                <small>{{ signal.channel_name }} · {{ formatLocalDateTime(signal.observed_at) }}</small>
                <ExpandableText class="tm-alpha-expandable-copy" :text="signal.thesis || t('alpha.noThesis')" :lines="2" />
                <small v-if="projectMarketFacts(signal)" class="tm-alpha-project-market">{{ projectMarketFacts(signal) }}</small>
                <small v-if="propagationSummary(signal)" class="tm-alpha-propagation">{{ propagationSummary(signal) }}</small>
                <small v-if="earlyScoreDetails(signal)" class="tm-alpha-early-score-detail">{{ earlyScoreDetails(signal) }}</small>
              </span>
              <span class="tm-alpha-project-score" :title="earlyScoreDetails(signal)">
                <strong>{{ (earlyProjectScore(signal)?.early_score ?? signal.integrity_score).toFixed(0) }}</strong>
                <small>{{ earlyProjectScore(signal) ? t('alpha.earlyScore') : t('alpha.integrity') }}</small>
              </span>
            </div>
          </div>
          <div v-else class="tm-alpha-empty">{{ t('alpha.noEarlyProjects') }}</div>
        </article>
      </div>

      <article class="tm-card tm-alpha-validation-card">
        <header><div><Activity :size="16" /><strong>{{ t('alpha.projectValidation') }}</strong></div><span>{{ t('alpha.projectValidationHint') }}</span></header>
        <div v-if="earlyProjects.length" class="tm-alpha-validation-table">
          <div v-for="signal in earlyProjects" :key="`validation-${signal.id}`" class="tm-alpha-validation-row">
            <span class="tm-alpha-token">
              <strong v-if="signal.asset_symbol?.trim() || signal.project_name?.trim()">{{ signalEntityLabel(signal) }}</strong>
              <CopyableAddress v-else-if="signal.contract_address" :value="signal.contract_address" />
              <strong v-else>—</strong>
              <small>{{ signal.chain || t('alpha.unknownChain') }}</small>
            </span>
            <span class="tm-alpha-validation-exante">
              <strong>{{ (earlyProjectScore(signal)?.early_score ?? signal.integrity_score).toFixed(0) }}</strong>
              <small>{{ t('alpha.earlyScore') }} · {{ projectPairAge(signal) }}</small>
            </span>
            <span class="tm-alpha-validation-discovery">
              <strong>{{ signalSender(signal) }}</strong>
              <small>{{ formatLocalDateTime(signal.observed_at) }}</small>
            </span>
            <span class="tm-alpha-validation-outcomes">
              <span class="tm-alpha-validation-horizon" :class="projectValidationClass(signal, 86_400)">
                <small>24h</small><strong>{{ projectValidationLabel(signal, 86_400) }}</strong>
              </span>
              <span class="tm-alpha-validation-horizon" :class="projectValidationClass(signal, 604_800)">
                <small>7d</small><strong>{{ projectValidationLabel(signal, 604_800) }}</strong>
              </span>
            </span>
            <span class="tm-alpha-validation-excursion">
              <strong>{{ projectExcursion(signal) }}</strong>
              <small>{{ projectValidationSource(signal) }}</small>
            </span>
          </div>
        </div>
        <div v-else class="tm-alpha-empty">{{ t('alpha.noProjectValidation') }}</div>
      </article>

      <article class="tm-card tm-alpha-lifecycle-card">
        <header><div><Radar :size="16" /><strong>{{ t('alpha.projectLifecycle') }}</strong></div><span>{{ t('alpha.projectLifecycleHint') }}</span></header>
        <div v-if="projectLifecycles.length" class="tm-alpha-lifecycle-list">
          <div v-for="lifecycle in projectLifecycles" :key="`${lifecycle.chain}-${lifecycle.contract_address || lifecycle.asset_symbol || lifecycle.project_name}`" class="tm-alpha-lifecycle-row">
            <span class="tm-alpha-token">
              <strong v-if="lifecycle.asset_symbol?.trim() || lifecycle.project_name?.trim()">{{ lifecycleEntity(lifecycle) }}</strong>
              <CopyableAddress v-else-if="lifecycle.contract_address" :value="lifecycle.contract_address" />
              <strong v-else>—</strong>
              <small>{{ lifecycle.chain || t('alpha.unknownChain') }}</small>
            </span>
            <span class="tm-alpha-lifecycle-state" :class="`tm-alpha-lifecycle-state--${lifecycle.state}`">{{ lifecycleStateLabel(lifecycle.state) }}</span>
            <span class="tm-alpha-lifecycle-track">
              <i v-for="milestone in lifecycle.milestones" :key="`${milestone.milestone_type}-${milestone.signal_id}-${milestone.observed_at}`" :class="`tm-alpha-lifecycle-step--${milestone.milestone_type}`" :title="`${formatLocalDateTime(milestone.observed_at)} · ${milestone.detail}`">
                {{ lifecycleMilestoneLabel(milestone.milestone_type) }}
              </i>
            </span>
            <span class="tm-alpha-lifecycle-source">
              <strong>{{ lifecycleDiscoverer(lifecycle) }}</strong>
              <small>{{ lifecycle.first_source }} · {{ lifecycle.independent_senders }} {{ t('alpha.independentPeople') }} / {{ lifecycle.independent_sources }} {{ t('alpha.sources') }}</small>
            </span>
            <span class="tm-alpha-lifecycle-market">
              <strong>{{ lifecycleMarket(lifecycle) }}</strong>
              <small>{{ lifecycle.total_signals }} {{ t('alpha.signalsShort') }} · {{ formatLocalDateTime(lifecycle.last_activity_at) }}</small>
            </span>
          </div>
        </div>
        <div v-else class="tm-alpha-empty">{{ t('alpha.noProjectLifecycle') }}</div>
      </article>

      <article class="tm-card tm-alpha-propagation-card">
        <header><div><Network :size="16" /><strong>{{ t('alpha.propagationTitle') }}</strong></div><span>{{ t('alpha.propagationHint') }}</span></header>
        <div v-if="propagationClusters.length" class="tm-alpha-propagation-list">
          <template v-for="cluster in propagationClusters" :key="cluster.first_signal_id">
            <button type="button" class="tm-alpha-propagation-row" @click="togglePropagation(cluster)">
              <span class="tm-alpha-token">
                <strong v-if="cluster.asset_symbol?.trim() || cluster.project_name?.trim()">{{ propagationEntityLabel(cluster) }}</strong>
                <CopyableAddress v-else-if="cluster.contract_address" :value="cluster.contract_address" />
                <strong v-else>—</strong>
                <small>{{ cluster.chain || t('alpha.unknownChain') }}</small>
              </span>
              <span class="tm-alpha-propagation-origin">
                <strong>{{ firstSeenSender(cluster) }}</strong>
                <small>{{ cluster.first_channel_name }} · {{ formatLocalDateTime(cluster.first_seen_at) }}</small>
              </span>
              <span class="tm-alpha-propagation-metrics">
                <strong>{{ cluster.independent_senders }} {{ t('alpha.independentPeople') }} · {{ cluster.independent_sources }} {{ t('alpha.sources') }}</strong>
                <small>{{ cluster.independent_signal_count }}/{{ cluster.total_signals }} {{ t('alpha.independentEvidence') }} · {{ propagationIndependence(cluster) }}</small>
              </span>
              <span class="tm-alpha-propagation-risk" :class="{ risky: cluster.duplicate_signal_count || cluster.near_duplicate_signal_count || cluster.copy_risk_signal_count || cluster.coordinated_shill_signal_count }">
                <strong>{{ propagationRisk(cluster) }}</strong>
                <small>{{ propagationWindow(cluster.propagation_minutes) }} · ↑{{ cluster.bullish_signal_count }} ↓{{ cluster.bearish_signal_count }}</small>
              </span>
              <component :is="expandedPropagationId === cluster.first_signal_id ? ChevronUp : ChevronDown" :size="14" class="tm-alpha-propagation-toggle" />
            </button>
            <div v-if="expandedPropagationId === cluster.first_signal_id" class="tm-alpha-propagation-detail">
              <div v-for="event in cluster.timeline" :key="event.signal_id" class="tm-alpha-propagation-event" :class="`tm-alpha-propagation-event--${event.classification}`">
                <span class="tm-alpha-propagation-event-marker"></span>
                <div class="tm-alpha-propagation-event-body">
                  <div class="tm-alpha-propagation-event-head">
                    <strong>{{ propagationEventSender(event) }}</strong>
                    <span>{{ event.channel_name }}</span>
                    <time>{{ formatLocalDateTime(event.observed_at) }}</time>
                    <em>{{ propagationClassification(event) }}</em>
                    <small v-if="propagationSimilarity(event)">{{ propagationSimilarity(event) }}</small>
                    <small v-if="event.telegram_message_id !== null">#{{ event.telegram_message_id }}</small>
                  </div>
                  <p>{{ event.source_text || event.thesis || t('alpha.noSourceText') }}</p>
                  <small v-if="event.duplicate_of_signal_id" class="tm-alpha-duplicate-link">{{ t('alpha.duplicateOf') }} {{ event.duplicate_of_signal_id.slice(0, 8) }}</small>
                  <div v-if="supportingEvidence(event).length" class="tm-alpha-supporting-evidence">
                    <span>{{ t('alpha.supportingEvidence') }}</span>
                    <div v-for="message in supportingEvidence(event)" :key="message.message_id">
                      <strong>{{ message.sender_username ? `@${message.sender_username}` : (message.sender_name || t('alpha.unknownSender')) }}</strong>
                      <small>{{ message.channel_name }} · #{{ message.telegram_message_id }} · {{ formatLocalDateTime(message.observed_at) }}</small>
                      <p>{{ message.text || t('alpha.noSourceText') }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!cluster.timeline.length" class="tm-alpha-empty">{{ t('alpha.noPropagationTimeline') }}</div>
            </div>
          </template>
        </div>
        <div v-else class="tm-alpha-empty">{{ t('alpha.noPropagation') }}</div>
      </article>

      <article class="tm-card tm-alpha-thread-card">
        <header><div><Crosshair :size="16" /><strong>{{ t('alpha.callThreads') }}</strong></div><span>{{ t('alpha.callThreadHint') }}</span></header>
        <div v-if="callThreads.length" class="tm-alpha-thread-list">
          <div v-for="thread in callThreads" :key="thread.id" class="tm-alpha-thread-row">
            <span class="tm-alpha-token">
              <strong v-if="thread.asset_symbol?.trim() || thread.project_name?.trim()">{{ threadEntity(thread) }}</strong>
              <CopyableAddress v-else-if="thread.contract_address" :value="thread.contract_address" />
              <strong v-else>—</strong>
              <small>{{ thread.chain || t('alpha.unknownChain') }}</small>
            </span>
            <span class="tm-alpha-thread-copy">
              <strong>{{ threadSender(thread) }} <em v-if="thread.incomplete_history">{{ t('alpha.incompleteHistory') }}</em></strong>
              <small>{{ thread.channels.join(' · ') }} · {{ formatLocalDateTime(thread.opened_at) }} → {{ formatLocalDateTime(thread.last_updated_at) }}</small>
              <span class="tm-alpha-thread-events">
                <i v-for="event in thread.events.slice(-6)" :key="event.signal_id" :class="`tm-alpha-event--${event.event_type}`">{{ event.event_type.toUpperCase() }}</i>
              </span>
              <ExpandableText v-if="thread.latest_thesis" class="tm-alpha-expandable-copy" :text="thread.latest_thesis" :lines="2" />
            </span>
            <span class="tm-alpha-thread-state">
              <strong :class="`tm-alpha-thread-status--${thread.status}`">{{ t(`alpha.threadStatus.${thread.status}`, thread.status) }}</strong>
              <small>{{ thread.call_count }}C · {{ thread.update_count }}U · {{ thread.exit_count }}E</small>
              <small :class="{ verified: threadOutcome(thread) }">{{ threadOutcomeSummary(thread) }}</small>
            </span>
          </div>
        </div>
        <div v-else class="tm-alpha-empty">{{ t('alpha.noCallThreads') }}</div>
      </article>

      <article class="tm-card tm-alpha-signal-table-card">
        <header><div><Activity :size="16" /><strong>{{ t('alpha.recentSignals') }}</strong></div><span>{{ t('alpha.signalHint') }}</span></header>
        <div v-if="recentSignals.length" class="tm-alpha-signal-table">
          <div v-for="signal in recentSignals" :key="signal.id" class="tm-alpha-signal-row">
            <div class="tm-alpha-signal-key">
              <span class="tm-alpha-event" :class="`tm-alpha-event--${signal.event_type}`" :title="signal.event_type">{{ signal.event_type }}</span>
              <span class="tm-alpha-signal-asset" :title="signal.contract_address || signalEntityLabel(signal)">
                <strong v-if="signal.asset_symbol?.trim() || signal.project_name?.trim()">{{ signalEntityLabel(signal) }}</strong>
                <CopyableAddress v-else-if="signal.contract_address" :value="signal.contract_address" />
                <strong v-else>—</strong>
                <small>{{ signal.direction }}</small>
              </span>
            </div>

            <div class="tm-alpha-signal-source">
              <div class="tm-alpha-signal-sender-line">
                <strong :title="signalSender(signal)">{{ signalSender(signal) }}</strong>
                <em v-if="signal.sender_kind !== 'human'" class="tm-sender-kind" :class="`tm-sender-kind--${signal.sender_kind}`">{{ senderKindLabel(signal.sender_kind) }}</em>
              </div>
              <small :title="signal.channel_name">{{ signal.channel_name }}</small>
            </div>

            <div class="tm-alpha-signal-analysis">
              <ExpandableText class="tm-alpha-signal-thesis" :text="signal.thesis || t('alpha.noThesis')" :lines="2" />
              <span class="tm-alpha-signal-meta" :title="`${t('alpha.structure')} ${signal.structure_score.toFixed(0)}`">
                <strong>{{ signal.integrity_score.toFixed(0) }}</strong>
                <small>{{ t('alpha.integrity') }}</small>
                <i>{{ signal.evidence_quality }}</i>
                <small>{{ signal.evidence_message_ids.length }} {{ t('alpha.evidence') }}</small>
              </span>
            </div>

            <div class="tm-alpha-signal-result">
              <span class="tm-alpha-signal-outcome" :class="{ verified: latestOutcome(signal) }" :title="outcomeDetails(signal)">{{ outcomeSummary(signal) }}</span>
              <time>{{ formatLocalDateTime(signal.observed_at) }}</time>
            </div>
          </div>
        </div>
        <div v-else class="tm-alpha-empty">{{ t('alpha.noSignals') }}</div>
      </article>
    </template>
  </section>
</template>
